import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileDown, Gauge, Loader2, Sheet, TrendingUp, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, downloadFile, formatApiError } from "@/lib/api";
import { fmtDate } from "@/lib/constants";

const Kpi = ({ label, valore, nota, tono = "slate", id }) => (
  <div className={`rounded-xl border border-${tono}-200 bg-${tono}-50/60 p-3`} data-testid={id}>
    <p className="font-mono text-[9px] font-semibold uppercase tracking-[0.14em] text-slate-500">{label}</p>
    <p className="mt-1 text-2xl font-bold tracking-tight text-slate-900">{valore}</p>
    {nota && <p className="mt-0.5 text-[11px] text-slate-500">{nota}</p>}
  </div>
);

/** Consuntivo di fine sprint: giorni stimati contro giorni realmente erosi. */
export const Consuntivo = ({ projectId, sprint }) => {
  const [dati, setDati] = useState(null);
  const [aperto, setAperto] = useState(false);
  const [busy, setBusy] = useState("");

  const scarica = async (formato) => {
    setBusy(formato);
    try {
      const ext = formato === "pdf" ? "pdf" : "xlsx";
      await downloadFile(
        `/projects/${projectId}/consuntivo/export/${formato}?sprint=${encodeURIComponent(sprint || "")}`,
        `Consuntivo_${(sprint || "progetto").replace(/\s/g, "")}.${ext}`
      );
      toast.success(`Consuntivo ${ext.toUpperCase()} scaricato`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setBusy("");
  };

  useEffect(() => {
    if (!projectId || !sprint) return;
    setDati(null);
    api
      .get(`/projects/${projectId}/consuntivo`, { params: { sprint } })
      .then(({ data }) => setDati(data))
      .catch((e) => toast.error(formatApiError(e)));
  }, [projectId, sprint]);

  if (!dati) {
    return (
      <div className="grid h-28 place-items-center rounded-xl border border-slate-200 bg-white" data-testid="consuntivo-loading">
        <Loader2 className="h-5 w-5 animate-spin text-slate-300" />
      </div>
    );
  }

  const k = dati.kpi;
  const segno = (n) => (n > 0 ? `+${n}` : `${n}`);

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="consuntivo-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-2 text-lg font-semibold text-slate-900">
            <Gauge className="h-4 w-4 text-indigo-600" /> Stima contro effettivo
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-slate-500">
            Un task si chiude con l'OK del tester: l'effettivo è calcolato in giorni lavorativi dal primo
            avvio dei lavori fino all'approvazione.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" className="h-10" onClick={() => scarica("excel")}
                  disabled={busy === "excel"} data-testid="consuntivo-export-excel-button">
            {busy === "excel" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sheet className="mr-2 h-4 w-4" />}
            Excel
          </Button>
          <Button variant="outline" className="h-10" onClick={() => scarica("pdf")}
                  disabled={busy === "pdf"} data-testid="consuntivo-export-pdf-button">
            {busy === "pdf" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileDown className="mr-2 h-4 w-4" />}
            PDF
          </Button>
          <Button variant="outline" className="h-10" onClick={() => setAperto(true)} data-testid="consuntivo-detail-button">
            <TrendingUp className="mr-2 h-4 w-4" /> Dettaglio completo
          </Button>
        </div>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi id="consuntivo-stima" label="Giorni stimati" valore={`${k.stima_totale} gg`}
             nota={`${k.task_totali} task dello sprint`} />
        <Kpi id="consuntivo-effettivo" label="Giorni erosi" valore={`${k.effettivo_totale} gg`}
             nota={`${k.erosione_perc}% della stima`} />
        <Kpi id="consuntivo-scostamento" label="Scostamento" valore={`${segno(k.scostamento_totale)} gg`}
             nota={k.scostamento_totale > 0 ? "oltre la stima" : "entro la stima"} />
        <Kpi id="consuntivo-fuori" label="Task fuori stima" valore={k.task_fuori_stima}
             nota={`${k.task_chiusi} chiusi su ${k.task_totali} · ${k.rework_totale} rework`} />
      </div>

      {dati.fuori_stima.length > 0 && (
        <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 p-3" data-testid="consuntivo-fuori-stima">
          <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-rose-600">
            Task oltre la stima
          </p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {dati.fuori_stima.slice(0, 12).map((t) => (
              <span key={t.codice} data-testid={`fuori-stima-${t.codice}`}
                    className="rounded-full border border-rose-200 bg-white px-2 py-0.5 font-mono text-[11px] text-rose-700">
                {t.codice} {segno(t.scostamento)}gg
              </span>
            ))}
          </div>
        </div>
      )}

      {aperto && (
        <div className="fixed inset-0 z-[9997] flex items-center justify-center bg-slate-900/50 p-4"
             onClick={() => setAperto(false)}>
          <div className="max-h-[85vh] w-full max-w-5xl overflow-hidden rounded-2xl bg-white shadow-2xl"
               onClick={(e) => e.stopPropagation()} data-testid="consuntivo-modal">
            <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-4">
              <div>
                <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-indigo-600">
                  Consuntivo {sprint}
                </p>
                <h3 className="mt-1 text-lg font-semibold text-slate-900">Dettaglio stima contro effettivo</h3>
                <p className="mt-1 text-xs text-slate-500">
                  Solo task chiusi: {k.stima_chiusi} gg stimati contro {k.effettivo_chiusi} gg erosi
                  ({segno(k.scostamento_chiusi)} gg)
                </p>
              </div>
              <button onClick={() => setAperto(false)} data-testid="consuntivo-modal-close"
                      className="rounded-md p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-900">
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="max-h-[65vh] overflow-auto px-5 py-4">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-white">
                  <tr className="border-b border-slate-200 text-left">
                    {["Codice", "Task", "Assegnatario", "Stato", "Stimato", "Eroso", "Scostamento", "Rework", "Chiusura"].map((h) => (
                      <th key={h} className="px-2 py-2 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {dati.task.map((t) => (
                    <tr key={t.codice} className={`border-b border-slate-100 ${t.fuori_stima ? "bg-rose-50/50" : ""}`}
                        data-testid={`consuntivo-row-${t.codice}`}>
                      <td className="whitespace-nowrap px-2 py-2 font-mono text-xs font-semibold text-slate-900">{t.codice}</td>
                      <td className="max-w-[260px] px-2 py-2 text-slate-700">{t.task}</td>
                      <td className="whitespace-nowrap px-2 py-2 text-xs text-slate-500">{t.assegnatario || "-"}</td>
                      <td className="whitespace-nowrap px-2 py-2 text-xs text-slate-500">{t.stato}</td>
                      <td className="whitespace-nowrap px-2 py-2 font-mono text-xs text-slate-700">{t.stima} gg</td>
                      <td className="whitespace-nowrap px-2 py-2 font-mono text-xs text-slate-700">{t.effettivo} gg</td>
                      <td className={`whitespace-nowrap px-2 py-2 font-mono text-xs font-semibold ${t.scostamento > 0 ? "text-rose-700" : "text-emerald-700"}`}>
                        {segno(t.scostamento)} gg
                      </td>
                      <td className="whitespace-nowrap px-2 py-2 font-mono text-xs text-slate-500">{t.rework}</td>
                      <td className="whitespace-nowrap px-2 py-2 text-xs text-slate-500">
                        {t.chiuso ? fmtDate(t.chiusura) : "in corso"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Consuntivo;
